import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ClipboardList,
  ArrowLeft,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Check,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { createQuartersService } from "../../../services/quartersService";
import { useToast } from "../../../common_components/Toast";
import { QuartersApplicationStatus, matchStatus } from "../../../types/status";
import type { IApplication } from "../types/quarters.types";

const ApplicationList = () => {
  const navigate = useNavigate();
  const { apiRequest, user, isSuperAdmin } = useAuth();
  const quartersService = useMemo(() => createQuartersService({ apiRequest }), [apiRequest]);
  const toast = useToast();

  const [applications, setApplications] = useState<IApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(QuartersApplicationStatus.SUBMITTED);
  
  // Filtering
  const [filters, setFilters] = useState({
    quarterType: "",
    q: "",
    page: 1,
    limit: 20,
    sort: "-score",
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      const isPrivileged = isSuperAdmin;

      if (isPrivileged) {
        // Admin listings
        const res = await quartersService.getApplications({
          ...filters,
          status: activeTab || undefined,
          quarterType: filters.quarterType || undefined,
          q: filters.q || undefined,
        });
        if (res.success) {
          setApplications(res.data);
          setTotalPages(res.totalPages || 1);
          setTotalCount(res.total || 0);
        }
      } else {
        // Employee listings
        const res = await quartersService.getMyApplications();
        if (res.success) {
          setApplications(res.data);
          setTotalCount(res.data.length);
          setTotalPages(1);
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to load housing applications");
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters, isSuperAdmin, quartersService, toast]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const isPrivileged = isSuperAdmin;

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <button
            onClick={() => navigate("/quarters")}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-bold mb-2 group transition-colors"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight font-serif flex items-center gap-3">
            <ClipboardList className="w-8 h-8 text-blue-600" /> Housing Allotment Applications
          </h1>
        </div>
      </div>

      {/* Tabs (Only visible to privileged users) */}
      {isPrivileged && (
        <div className="flex border-b border-slate-200 mb-6 overflow-x-auto gap-2">
          {[
            { id: QuartersApplicationStatus.SUBMITTED, label: "Submitted", icon: Clock },
            { id: QuartersApplicationStatus.VERIFIED, label: "Verified", icon: CheckCircle },
            { id: QuartersApplicationStatus.REJECTED, label: "Rejected", icon: XCircle },
            { id: QuartersApplicationStatus.RANKED, label: "Ranked", icon: Check },
            { id: QuartersApplicationStatus.WAITING, label: "Waiting List", icon: Clock },
            { id: QuartersApplicationStatus.ALLOTTED, label: "Allotted Quarters", icon: CheckCircle },
            { id: QuartersApplicationStatus.DRAFT, label: "Drafts", icon: Clock },
          ].map((tab) => {
            const IconComp = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setFilters({ ...filters, page: 1 });
                }}
                className={`flex items-center gap-1.5 px-4 py-3 text-xs font-bold border-b-2 uppercase tracking-wide transition whitespace-nowrap ${activeTab === tab.id
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                  }`}
              >
                <IconComp className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Search & Filters */}
      {isPrivileged && (
        <div className="bg-white border border-slate-200 p-4 rounded-2xl mb-8 shadow-sm grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-400 uppercase">Quarter Type</label>
            <select
              value={filters.quarterType}
              onChange={(e) => setFilters({ ...filters, quarterType: e.target.value, page: 1 })}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
            >
              <option value="">All Types</option>
              <option value="Type I">Type I</option>
              <option value="Type II">Type II</option>
              <option value="Type III">Type III</option>
              <option value="Type IV">Type IV</option>
              <option value="Type V">Type V</option>
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-400 uppercase">Sorting</label>
            <select
              value={filters.sort}
              onChange={(e) => setFilters({ ...filters, sort: e.target.value, page: 1 })}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
            >
              <option value="-score">Highest Score First</option>
              <option value="score">Lowest Score First</option>
              <option value="-submittedAt">Newest Applications</option>
              <option value="submittedAt">Oldest Applications</option>
            </select>
          </div>

          <div className="space-y-1 flex gap-2 md:col-span-2">
            <div className="w-full relative">
              <input
                type="text"
                placeholder="Search app no / employee ID..."
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                className="w-full border border-slate-200 rounded-xl pl-8 pr-3 py-2 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 transition"
              />
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
            </div>
            <button
              onClick={loadApplications}
              className="bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 text-xs font-bold rounded-xl transition"
            >
              Search
            </button>
          </div>
        </div>
      )}

      {/* Grid List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white border border-slate-200 rounded-3xl">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-slate-500 text-sm font-semibold mt-3">Loading housing applications...</p>
        </div>
      ) : applications.length > 0 ? (
        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100 text-slate-500 text-xs font-semibold uppercase">
                  <th className="px-6 py-4 text-left">Application No.</th>
                  <th className="px-6 py-4 text-left">Employee Details</th>
                  <th className="px-6 py-4 text-left">Preferred Quarter</th>
                  <th className="px-6 py-4 text-left">Seniority details</th>
                  <th className="px-6 py-4 text-center">Score</th>
                  <th className="px-6 py-4 text-left">Status</th>
                  <th className="px-6 py-4 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                {applications.map((app) => (
                  <tr key={app._id} className="hover:bg-slate-50/30 transition">
                    <td className="px-6 py-4 font-bold text-slate-800">
                      {app.applicationNumber || <span className="text-slate-400 italic">Draft</span>}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-800">
                        {typeof app.userId === "object"
                          ? (app.userId as { name: string }).name
                          : user?.name}
                      </p>
                      <p className="text-slate-400 text-xs">
                        ID: {app.employeeDetails?.employeeId} | Dept: {app.employeeDetails?.department}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="bg-blue-50 text-blue-700 text-xs font-bold px-2.5 py-1 rounded-lg">
                        {app.quarterType}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-500">
                      <p>Service: {app.employeeDetails?.serviceYears} Years</p>
                      <p>PWD Status: {app.employeeDetails?.isPwd ? "Yes" : "No"}</p>
                      {app.submittedAt && <p>Submitted: {new Date(app.submittedAt).toLocaleDateString()}</p>}
                    </td>
                    <td className="px-6 py-4 text-center font-mono font-bold text-indigo-600 text-base">
                      {app.score || 0}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`text-xs px-2.5 py-1 rounded-full font-bold uppercase ${
                          matchStatus(app.status, QuartersApplicationStatus.DRAFT)
                            ? "bg-slate-100 text-slate-800"
                            : matchStatus(app.status, QuartersApplicationStatus.SUBMITTED)
                              ? "bg-blue-100 text-blue-800"
                              : matchStatus(app.status, QuartersApplicationStatus.VERIFIED) || matchStatus(app.status, QuartersApplicationStatus.RANKED)
                                ? "bg-indigo-100 text-indigo-800"
                                : matchStatus(app.status, QuartersApplicationStatus.ALLOTTED)
                                  ? "bg-emerald-100 text-emerald-800"
                                  : "bg-red-100 text-red-800"
                          }`}
                      >
                        {app.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-1.5">
                        {matchStatus(app.status, QuartersApplicationStatus.DRAFT) && !isPrivileged ? (
                          <button
                            onClick={() => navigate(`/quarters/apply/${app._id}`)}
                            className="bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs font-bold px-3 py-1.5 rounded-lg transition"
                          >
                            Edit
                          </button>
                        ) : (
                          <button
                            onClick={() => navigate(`/quarters/application/${app._id}`)}
                            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            title="View Application Details"
                          >
                            <Eye className="w-4.5 h-4.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {isPrivileged && totalPages > 1 && (
            <div className="flex justify-between items-center bg-slate-50/50 px-6 py-4 border-t border-slate-100">
              <span className="text-xs text-slate-500">
                Showing {applications.length} of {totalCount} applications
              </span>
              <div className="flex gap-2">
                <button
                  disabled={filters.page === 1}
                  onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 border border-slate-200 rounded-xl transition disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  disabled={filters.page === totalPages}
                  onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 border border-slate-200 rounded-xl transition disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-20 bg-white border border-slate-200 rounded-3xl">
          <ClipboardList className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-slate-700">No Applications</h2>
          <p className="text-slate-500 text-sm mt-1">There are no housing applications matching this filter.</p>
        </div>
      )}
    </div>
  );
};

export default ApplicationList;
